// useBarCollapse — manages chat-bar collapsed/expanded state.
//
// Collapse triggers:
//   - tap chevron handle
//   - swipe-down on the bar (via touch handlers in ChatInput)
//   - user scrolls UP away from the bottom (past COLLAPSE threshold)
//
// Expand triggers:
//   - tap chevron handle
//   - swipe-up on the handle strip
//   - user scrolls back to near-bottom (within EXPAND threshold)
//   - keyboard focus lands on any bar control
//
// Hysteresis: collapse at >120px from bottom, expand at <20px.
// The 20-120px dead zone prevents oscillation (bar resize shifts
// scroll position → re-triggers → flicker loop).

import { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_COLLAPSE_THRESHOLD = 120;
const SCROLL_EXPAND_THRESHOLD = 20;
const SCROLL_DEBOUNCE_MS = 80;
const LAYOUT_SETTLE_MS = 150;

export function useBarCollapse() {
  const [barExpanded, setBarExpanded] = useState(true);
  const [isScrolledAway, setIsScrolledAway] = useState(false);
  const timerRef = useRef<number | null>(null);
  const settleRef = useRef(false);
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

  const setBarWithSettle = useCallback((next: boolean) => {
    setBarExpanded(prev => {
      if (prev === next) return prev;
      settleRef.current = true;
      window.setTimeout(() => { settleRef.current = false; }, LAYOUT_SETTLE_MS);
      return next;
    });
  }, []);

  const collapseBar = useCallback(() => setBarWithSettle(false), [setBarWithSettle]);
  const expandBar   = useCallback(() => setBarWithSettle(true), [setBarWithSettle]);
  const toggleBar   = useCallback(() => {
    setBarExpanded(prev => {
      settleRef.current = true;
      window.setTimeout(() => { settleRef.current = false; }, LAYOUT_SETTLE_MS);
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (!scrollContainer) return;

    function handleScroll() {
      if (timerRef.current !== null) return;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (!scrollContainer || settleRef.current) return;
        const distFromBottom =
          scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
        if (distFromBottom > SCROLL_COLLAPSE_THRESHOLD) {
          setBarWithSettle(false);
          setIsScrolledAway(true);
        } else if (distFromBottom < SCROLL_EXPAND_THRESHOLD) {
          setBarWithSettle(true);
          setIsScrolledAway(false);
        }
        // Dead zone (20-120px): keep current values — hysteresis prevents flicker
      }, SCROLL_DEBOUNCE_MS);
    }

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [scrollContainer, setBarWithSettle]);

  const scrollToLatest = useCallback(() => {
    if (!scrollContainer) return;
    scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
    setIsScrolledAway(false);
  }, [scrollContainer]);

  return {
    barExpanded,
    collapseBar,
    expandBar,
    toggleBar,
    setScrollContainer,
    prefersReducedMotion: prefersReducedMotion.current,
    isScrolledAway,
    scrollToLatest,
  };
}
